import * as React from 'preact/compat'
import cn from 'classnames'
import Api, { HttpError } from '../api'
import { loadStylesheet } from '../common/helpers'
import SVG from './SVG'
import Modal from './Modal'
import Loading from './Loading'
import formCss from '../../css/form.pcss.json'
import buttonsCss from '../../css/buttons.pcss.json'
import styles from '../../css/game-editor.pcss.json'
import commonCss from '../../css/common.pcss.json'
import { showToast } from './toast'

interface PropTypes {
  id: string,
  onClose: () => void,
  afterSave?: () => void
}

interface StateTypes {
  loading: boolean,
  isBanned: boolean,
  title: string,
  description: string,
  public: number | null,
  sending: boolean,
}

export default class CompilationCreator extends React.Component<PropTypes, StateTypes> {
  state: StateTypes = {
    loading: true,
    isBanned: false,
    title: '',
    description: '',
    public: null,
    sending: false,
  }

  modal = React.createRef<Modal>()

  componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('form')
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('game-editor')
  }

  componentDidMount() {
    if (this.props.id !== 'new') {
      Api.get<{
        title: string,
        isBanned: boolean,
        poster: string,
        large_poster: string,
        description: string,
        public: number
      }>({ url: 'compilations/info/' + this.props.id })
        .then(result => {
          this.setState({
            loading: false,
            isBanned: result.isBanned,
            title: result.title,
            description: result.description,
            public: result.public
          })
        })
        .catch((e) => {
          this.props.onClose()
          if (e instanceof HttpError) {
            showToast({
              type: 'error',
              text: e.responseText
            })
          } else {
            showToast({
              type: 'error',
              text: 'Произошла непредвиденная ошибка'
            })
          }
        })
    } else {
      Api.get({ url: `reviews/minimum-info` })
        .then((response) => {
          this.setState({
            loading: false,
            isBanned: response.user.isBanned
          })
        })
    }
  }

  onSubmit = (event: React.TargetedEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (this.state.public === null) {
      showToast({
        type: 'error',
        text: 'Выбери тип публичности подборки'
      })
      return
    }

    this.setState({
      sending: true
    })
    const formData = new FormData(event.currentTarget)
    Api.post<{
      id: number
    }>({ url: 'compilations/edit', body: formData })
      .then(async (compilation) => {
        if (this.props.afterSave) {
          await this.modal.current.close()
          this.props.afterSave()
        } else if (window.location.pathname.startsWith('/games/compilation/')) {
          window.location.reload()
        } else {
          window.location.href = '/games/compilation/' + compilation.id
        }
      })
      .catch((e) => {
        if (e instanceof HttpError) {
          showToast({
            type: 'error',
            text: e.responseText
          })
        } else {
          showToast({
            type: 'error',
            text: 'Произошла непредвиденная ошибка'
          })
        }
      })
      .finally(() => {
        this.setState({
          sending: false
        })
      })
  }

  setPrivacy = (type: number) => {
    this.setState({
      public: type
    })
  }

  render() {
    const {
      loading,
      isBanned,
      title,
      description,
      sending,
    } = this.state
    const { id } = this.props

    if (isBanned) {
      return <Modal onClose={this.props.onClose} showClose>
        <div className={styles['container']}>
          <h1>Ну это бан!</h1>
          <span>Ты в бане и не можешь создавать подборки</span>
        </div>
      </Modal>
    }

    return <Modal ref={this.modal} showClose onClose={this.props.onClose} mobileFullScreen>
      {loading && <Loading/>}
      {!loading && <>
        <form onSubmit={this.onSubmit} className={formCss['form-aligned']}>
          <h2 className={commonCss['h3']}>{this.props.id === 'new' ? 'Создание' : 'Редактирование'} подборки игр</h2>
          <input type='hidden' name='id' value={id}/>
          <div className={formCss['form-group--new']}>
            <label htmlFor='compilation_public'>Настройки публичности</label>
            <div style='display: flex; column-gap: 16px'>
              <input type='hidden' name='Compilation[public]' value={`${this.state.public}`}/>
              <button type='button' className={cn({
                [buttonsCss['gray']]: this.state.public !== 0,
                [buttonsCss['primary']]: this.state.public === 0
              })} onClick={() => this.setPrivacy(0)}>
                <SVG i='sg-new/eye-slash' adaptive/> Приватная
              </button>
              <button type='button' className={cn({
                [buttonsCss['gray']]: this.state.public !== 2,
                [buttonsCss['primary']]: this.state.public === 2
              })} onClick={() => this.setPrivacy(2)}>
                <SVG i='sg-new/user' adaptive/> Личная
              </button>
              <button type='button' className={cn({
                [buttonsCss['gray']]: this.state.public !== 1,
                [buttonsCss['primary']]: this.state.public === 1
              })} onClick={() => this.setPrivacy(1)}>
                <SVG i='sg-new/users' adaptive/> Публичная
              </button>
            </div>
            <p style='max-width: 680px; margin: 0; color: var(--gray-2)'>
              {this.state.public === null && 'Выбери тип публичности подборки и тут появится его объяснение'}
              {this.state.public === 1
                && <>Видна всем в разделе «Подборки игр», система рейтинга включена.<br/>Подходит для подборок в духе «Игры с интересными механиками» или «10 отличных игр, о которых вы не знали»</>
              }
              {this.state.public === 2
                && <>Видна всем в твоём профиле, система рейтинга выключена.<br/>Подходит для подборок в духе «Мой личный топ» или «Игры, которые сделали моё детство»</>
              }
              {this.state.public === 0
                && <>Видна только тебе в твоём профиле, система рейтинга выключена.<br/>Подходит для подборок в духе «Прошёл в 2023», «Купил, но не запускал» или «Бэклог»</>
              }
            </p>
          </div>
          <div className={formCss['form-group--new']}>
            <label>Название подборки</label>
            <input
              type='text'
              className={formCss['input']}
              name='Compilation[title]'
              defaultValue={title}
              max={255}
              required
            />
          </div>
          <div className={formCss['form-group--new']}>
            <label>Описание подборки (опционально)</label>
            <textarea className={formCss['input']} name='Compilation[description]'>{description}</textarea>
          </div>
          <div className={formCss['form-buttons-row']}>
            <button type='submit' className={buttonsCss['primary']} style='--button-padding: 16px' disabled={sending}>
              Сохранить
            </button>
          </div>
        </form>
      </>}
    </Modal>
  }
}
